import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { X, ChevronLeft, ChevronRight, Heart, Calendar, MapPin, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import SectionHeading from '../components/SectionHeading';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default gallery events (fallback if database is empty)
const defaultGalleryEvents = [
  {
    id: 'orphan-feeding-africa-2025',
    title: 'Orphan Feeding Program - Africa',
    description: 'Our volunteers distributing meals to orphaned children in need.',
    date: 'November 2025',
    location: 'Africa',
    images: [
      {
        url: 'https://customer-assets.emergentagent.com/job_e98123c3-9045-4374-a568-900995d0e648/artifacts/dymp47id_5f139bef-3b0b-462d-9194-a49e38c0a5db.jpg',
        alt: 'Group of orphaned children receiving meals',
        caption: 'Orphaned children gathered for meal distribution'
      },
      {
        url: 'https://customer-assets.emergentagent.com/job_e98123c3-9045-4374-a568-900995d0e648/artifacts/qz3o7no5_419c3fa5-463d-48c8-a1db-b859c9d25008.jpg',
        alt: 'Volunteer distributing food to children',
        caption: 'Volunteers serving meals to children with care and love'
      },
      {
        url: 'https://customer-assets.emergentagent.com/job_e98123c3-9045-4374-a568-900995d0e648/artifacts/6yh7fr18_43c8b78c-8249-4722-9c52-40355981cd8e.jpg',
        alt: 'Child receiving footwear donation',
        caption: 'Providing essential items including footwear to children in need'
      },
      {
        url: 'https://customer-assets.emergentagent.com/job_e98123c3-9045-4374-a568-900995d0e648/artifacts/swm0n68c_a5a8a7df-cd51-401b-a2a8-88ea702536db.jpg',
        alt: 'Community food preparation',
        caption: 'Community coming together to prepare meals for orphans'
      },
      {
        url: 'https://customer-assets.emergentagent.com/job_e98123c3-9045-4374-a568-900995d0e648/artifacts/3w67v7rf_PHOTO-2025-11-21-11-00-24.jpg',
        alt: 'Prepared meals for distribution',
        caption: 'Nutritious meals prepared with love for the children'
      }
    ]
  }
];

const GalleryPage = () => {
  const [galleryEvents, setGalleryEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Fetch gallery events from API
  useEffect(() => {
    const fetchGalleryEvents = async () => {
      try {
        const response = await axios.get(`${API}/gallery/events`);
        if (response.data && response.data.length > 0) {
          setGalleryEvents(response.data);
        } else {
          // Use default events if database is empty
          setGalleryEvents(defaultGalleryEvents);
        }
      } catch (error) {
        console.error('Error fetching gallery events:', error);
        // Fallback to default events on error
        setGalleryEvents(defaultGalleryEvents);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryEvents();
  }, []);

  const openLightbox = (event, imageIndex) => {
    setCurrentEvent(event);
    setCurrentImageIndex(imageIndex);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setCurrentEvent(null);
    document.body.style.overflow = 'unset';
  };

  const nextImage = () => {
    if (currentEvent) {
      setCurrentImageIndex((prev) => 
        prev === currentEvent.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (currentEvent) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? currentEvent.images.length - 1 : prev - 1
      );
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowRight') nextImage();
    if (e.key === 'ArrowLeft') prevImage();
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="py-16 lg:py-24 hero-gradient islamic-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Our Impact
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground font-['Outfit'] leading-tight mb-6">
              Event <span className="text-gradient">Gallery</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Witness the impact of your donations. See how True North Muslim Community is making 
              a difference in the lives of orphans and vulnerable families around the world.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-24 lg:py-32" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {galleryEvents.map((event, eventIndex) => (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-16 last:mb-0"
              data-testid={`gallery-event-${event.id}`}
            >
              {/* Event Header */}
              <div className="mb-8">
                <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-foreground mb-3">
                  {event.title}
                </h2>
                <p className="text-muted-foreground mb-4">{event.description}</p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    {event.date}
                  </span>
                  <span className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-primary" />
                    {event.location}
                  </span>
                </div>
              </div>

              {/* Image Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {event.images.map((image, imageIndex) => (
                  <motion.div
                    key={imageIndex}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: imageIndex * 0.1 }}
                    className={`relative group cursor-pointer overflow-hidden rounded-2xl ${
                      imageIndex === 0 ? 'sm:col-span-2 sm:row-span-2' : ''
                    }`}
                    onClick={() => openLightbox(event, imageIndex)}
                    data-testid={`gallery-image-${eventIndex}-${imageIndex}`}
                  >
                    <div className={`aspect-square ${imageIndex === 0 ? 'sm:aspect-[4/3]' : ''}`}>
                      <img
                        src={image.url}
                        alt={image.alt}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                    </div>
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <p className="text-white text-sm font-medium">{image.caption}</p>
                      </div>
                    </div>
                    {/* View indicator */}
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && currentEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            data-testid="lightbox"
          >
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Close lightbox"
              data-testid="lightbox-close"
            >
              <X className="h-6 w-6 text-white" />
            </button>

            {/* Navigation buttons */}
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-4 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Previous image"
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="h-6 w-6 text-white" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-4 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Next image"
              data-testid="lightbox-next"
            >
              <ChevronRight className="h-6 w-6 text-white" />
            </button>

            {/* Image */}
            <motion.div
              key={currentImageIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="max-w-5xl max-h-[80vh] px-16"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={currentEvent.images[currentImageIndex].url}
                alt={currentEvent.images[currentImageIndex].alt}
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
              <div className="mt-4 text-center">
                <p className="text-white text-lg font-medium">
                  {currentEvent.images[currentImageIndex].caption}
                </p>
                <p className="text-white/60 text-sm mt-2">
                  {currentImageIndex + 1} of {currentEvent.images.length}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-card islamic-pattern" data-testid="gallery-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHeading
            badge="Make an Impact"
            title="Help Us Reach More Children"
            description="Your donation directly supports orphan feeding programs and essential aid distribution."
          />
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/donate" data-testid="gallery-donate-btn">
              <Button size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-8 glow-teal">
                <Heart className="mr-2 h-5 w-5" />
                Donate Now
              </Button>
            </Link>
            <Link to="/programs#orphan-support" data-testid="gallery-programs-btn">
              <Button size="lg" variant="outline" className="rounded-full px-8">
                Learn About Our Programs
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;
